import { CreateTagRequest, CreateTopicRequest, CreateTopicResponse, GetTopicResponse, KlavioEventPropertyKey, KlayvioEvents, SearchFilter, TelemetryEntry, TopicAccessLevel, TopicSearchResult } from '../../types';
import { GoogleTagEvent, buildQueryString, del, emptyPost, get, logEventWithKlayvio, logWithAmplitude, logWithGoogleTag, logWithGoogleTagAndTopicOrganization, post, put, throwHttpError } from './api';
import { IFRAME_TEMPLATE, POWERED_BY_EMBED, TELEMETRY_ENDPOINT } from '../constants';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { getFromLocalStorage, isDefined, isHeadlessRenderContext, isValidString } from '../utils';
import { embedHost } from '../device';

import accountInfo from '../state/account-info';
import topicData from '../state/topic-data';

export const TEMPLATE_CATEGORIES = [
    {
        name: 'My Places',
        description:
            'You can edit this category and add others in "categories section" on the left side. ',
        icon: 'https://img.icons8.com/stickers/marker.png',
    },
];

export async function getTopics(
    amplitude_callsite: string,
    search_filter: SearchFilter
) {
    const response = await fetch(`/api/topics/search`, post(search_filter));

    if (!response.ok) {
        console.error('Error getting topics', response);
        return [];
    }

    logWithAmplitude(amplitude_callsite, 'Search Topics', {
        discovery_level: search_filter.discovery_level,
        keyword_search: search_filter.keyword_search?.keywords.length,
        minimum_point_count: search_filter.minimum_point_count,
        source_type: search_filter.source_type,
    });
    return await (response.json() as Promise<TopicSearchResult[]>);
}

export async function getTopicsforBackpack(
    amplitude_callsite: string,
) {
    if (!accountInfo.isAuthenticated) {
        return [];
    }

    const organization_id = getFromLocalStorage('organization_id');

    const queryString = buildQueryString({ organization_id });

    const response = await fetch(`/api/topics/backpack${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the team backpack topics.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Backpack Topics', {
        organization_id
    });
    return await (response.json() as Promise<TopicSearchResult[]>);
}


// Get topics with pagination and stream them to the frontend as they arrive.
// This is used for the backpack page where we want to show topics as they are fetched from the server.
// The onBatch callback is called with each batch of topics as they arrive.
// The onComplete callback is called when all batches have been fetched.
export async function getPaginatedTopicsforBackpack(
    amplitude_callsite: string,
    onBatch: (topics: TopicSearchResult[]) => void,
    onComplete?: (totalTopics: number) => void,
    limit: number = 50
): Promise<void> {
    if (!accountInfo.isAuthenticated) {
        onComplete?.(0);
        return;
    }

    const organization_id = getFromLocalStorage('organization_id');
    let offset = 0;
    let hasMore = true;
    let totalTopics = 0;

    try {
        while (hasMore) {
            const queryString = buildQueryString({ organization_id, limit, offset });
            const response = await fetch(`/api/topics/backpack${queryString}`, get());

            if (!response.ok) {
                await throwHttpError('An error occurred while getting the backpack topics.', response);
            }

            const topics = await (response.json() as Promise<TopicSearchResult[]>);

            // Push this batch to the frontend immediately
            if (topics.length > 0) {
                onBatch(topics);
                totalTopics += topics.length;
            }

            // If we got fewer topics than the limit, we've reached the end
            hasMore = topics.length === limit;
            offset += limit;
        }

        logWithAmplitude(amplitude_callsite, 'Get Paginated Backpack Topics', {
            organization_id,
            total_topics: totalTopics,
        });
    } finally {
        // Always notify completion, even if an error occurred, to reset loading state
        onComplete?.(totalTopics);
    }
}

export async function getPersonalTopicsforBackpack(
    amplitude_callsite: string
) {
    const response = await fetch(`/api/topics/backpack`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the personal backpack topics.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Personal Backpack Topics');
    return await (response.json() as Promise<TopicSearchResult[]>);
}


export async function getBookmarksforBackpack(
    amplitude_callsite: string
) {
    const response = await fetch(`/api/topics/bookmarks`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the bookmark topics.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Bookmark Backpack Topics');
    return await (response.json() as Promise<TopicSearchResult[]>);
}

export async function getContributedToTopics(
    amplitude_callsite: string
) {
    const queryString = buildQueryString({ email: accountInfo.email });

     const response = await fetch(`/api/topics/contributions${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the contributed to topics.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Contributed To Topics');
    return await (response.json() as Promise<TopicSearchResult[]>);
}

export async function getTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string,
    use_cache?: boolean,
    point_id?: string,
    point_key?: string,
) {
    const queryString = buildQueryString({
        topic_key,
        use_cache,
        point_id,
        point_key,
    });
    const response = await fetch(
        `/api/topics/${topic_id}${queryString}`,
        get()
    );

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the topic.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Topic', {
        topic_id,
        topic_key,
        use_cache,
    });
    return await (response.json() as Promise<GetTopicResponse>);
}


export async function addTopic(
    amplitude_callsite: string,
    topic: CreateTopicRequest,
    additional_logged_parameters: Object = {}
) {
    const organization_id = getFromLocalStorage('organization_id');
    if (organization_id !== null) {
        topic.organization_id = { $oid: organization_id };
    }

    const topic_response = await addTopicInner(topic);
    logWithAmplitude(
        amplitude_callsite,
        'Topic Created',
        Object.assign(
            {},
            {
                topic_id: topic_response._id,
                map_name: topic.name,
                email: topic.email,
                map_center: topic.default_map_center,
                map_zoom: topic.default_map_zoom,
                num_categories: topic.categories?.length,
            },
            additional_logged_parameters
        )
    );

    if (isDefined(topic.email)) {
        logEventWithKlayvio(KlayvioEvents.MapCreated, topic.email ?? accountInfo.email, [
            { key: KlavioEventPropertyKey.MapName, value: topic.name ?? '' },
            { key: KlavioEventPropertyKey.MapId, value: topic_response._id.$oid },
            { key: KlavioEventPropertyKey.ReadOnlyLink, value: topic_response.cc_read_link },
            { key: KlavioEventPropertyKey.EditorLink, value: topic_response.cc_account_admin_link }
        ]);
    }

    logWithGoogleTag(amplitude_callsite, GoogleTagEvent.TOPIC_CREATED, {
        topic_id: topic_response._id,
        topic_name: topic.name,
    });

    return topic_response;
}

async function addTopicInner(topic: CreateTopicRequest) {
    const response = await fetch(`/api/topics`, post(topic));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the topic.', response);
    }

    return await (response.json() as Promise<CreateTopicResponse>);
}

export async function updateTopicAndPointsWithEmail(amplitude_callsite: string, topic_id: string, email: string, topic_key: string, number_of_points: number): Promise<Boolean> {
    const response = await fetch(`/api/topics/${topic_id}/email/${email}${buildQueryString({ topic_key })}`, emptyPost());
    if (!response.ok) {
        await throwHttpError('An error occurred while updating email for a topic.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Updated topic and point with email',
        {
            topic_id,
            topic_key,
            num_points: number_of_points
        }
    );

    logEventWithKlayvio(KlayvioEvents.MapCreated, email ?? accountInfo.email, [
        { key: KlavioEventPropertyKey.MapId, value: topic_id },
    ]);

    return true;
}

export async function deleteKeysForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/keys${queryString}`, del());

    if (!response.ok) {
        await throwHttpError('An error occurred while cycling the topic keys.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Delete Keys', {
        topic_id,
        topic_key,
    });
}

export async function editVanityRedirectKeyForTopic(
    amplitude_callsite: string,
    topic_id: string,
    redirect_key: string,
    topic_key?: string
) {

    const queryString = buildQueryString({ topic_key });
    const response = await fetch(
        `/api/topics/${topic_id}/vanity_redirect/${queryString}`,
        put({ key: redirect_key })
    );
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the vanity redirect.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Update Redirect URL', {
        topic_id,
    });
    return await Promise.resolve();
}

export async function updateTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic: CreateTopicRequest,
    topic_key?: string,
    additional_logged_parameters: Object = {}
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}${queryString}`, put(topic));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the topic.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Topic Updated',
        Object.assign(
            {},
            {
                topic_id,
                topic_key,
                num_categories: topic.categories?.length,
            },
            additional_logged_parameters
        )
    );

    if (isDefined(topic.email)) {
        logEventWithKlayvio(KlayvioEvents.MapUpdated, accountInfo.email ?? getFromLocalStorage('email') ?? '', [
            { key: KlavioEventPropertyKey.MapName, value: topic.name ?? '' },
            { key: KlavioEventPropertyKey.MapId, value: topic_id },
            { key: KlavioEventPropertyKey.ReadOnlyLink, value: topicData.topic?.cc_read_link ?? '' },
            { key: KlavioEventPropertyKey.EditorLink, value: topicData.topic?.cc_account_admin_link ?? '' }
        ]);
    }

}

export async function deleteTopic(
    amplitude_callsite: string,
    topic_id: string
) {
    const response = await fetch(`/api/topics/${topic_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the topic.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Topic Deleted', {
        topic_id
    });


    logEventWithKlayvio(KlayvioEvents.MapDeleted, accountInfo.email, [
        { key: KlavioEventPropertyKey.MapId, value: topic_id },
    ]);

}

export async function addTopicView(amplitude_callsite: string, topic_id: string, organization_id?: string) {
    if (isHeadlessRenderContext()) {
        return;
    }

    let qs = '';
    if (organization_id) {
        qs = `?organization_id=${organization_id}`;
    }
    const response = await fetch(`${TELEMETRY_ENDPOINT}/api/topics/${topic_id}/view${qs}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while adding the topic view.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Add Topic View', {
        topic_id,
    });
    const embed_host = embedHost();
    logWithGoogleTagAndTopicOrganization(amplitude_callsite, GoogleTagEvent.MAP_VIEW, embed_host === undefined ? {} : { embed_host });
    return await Promise.resolve();
}

export async function addTopicRender(amplitude_callsite: string, topic_id: string, organization_id?: string) {
    if (isHeadlessRenderContext()) {
        return;
    }

    let qs = '';
    if (organization_id) {
        qs = `?organization_id=${organization_id}`;
    }

    const response = await fetch(`${TELEMETRY_ENDPOINT}/api/topics/${topic_id}/render${qs}`, emptyPost());

    if (!response.ok) {
        await throwHttpError('An error occurred while adding the topic render.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Add Topic Render', {
        topic_id,
    });
    logWithGoogleTagAndTopicOrganization(amplitude_callsite, GoogleTagEvent.MAP_RENDER);
    return await Promise.resolve();
}

export async function addPointView(amplitude_callsite: string, topic_id: string, point_id: string, card_type?: string) {
    const response = await fetch(
        `${TELEMETRY_ENDPOINT}/api/topics/${topic_id}/points/${point_id}/view`,
        emptyPost()
    );

    if (!response.ok) {
        await throwHttpError('An error occurred while adding the point view.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Add Point View', {
        point_id,
    });
    logWithGoogleTagAndTopicOrganization(amplitude_callsite, GoogleTagEvent.POINT_CLICKED, {
        point_id,
        card_type: card_type ?? 'unknown',
    });
    return await Promise.resolve();
}

export async function addChallengeView(amplitude_callsite: string, topic_id: string, challenge_id: string) {
    const response = await fetch(
        `${TELEMETRY_ENDPOINT}/api/topics/${topic_id}/challenges/${challenge_id}/view`,
        emptyPost()
    );

    if (!response.ok) {
        await throwHttpError('An error occurred while adding the challenge view.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Add Challenge View', {
        challenge_id,
    });
    return await Promise.resolve();
}

export async function addOpenTableClick(
    amplitude_callsite: string,
    topic_id: string,
    point_id: string
) {
    const response = await fetch(
        `${TELEMETRY_ENDPOINT}/api/topics/${topic_id}/points/${point_id}/opentable_click`,
        emptyPost()
    );

    if (!response.ok) {
        await throwHttpError('An error occurred while adding the opentable click.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Add OpenTable Click', {
        point_id,
    });
    logWithGoogleTagAndTopicOrganization(amplitude_callsite, GoogleTagEvent.POINT_OPEN_TABLE_CLICKED, {
        point_id,
    });
    return await Promise.resolve();
}

export async function getViewsForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/views${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the topic views.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Topic Views', {
        topic_id,
        topic_key,
    });
    return await (response.json() as Promise<TelemetryEntry[]>);
}


export async function getPointViewsForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/point_views${queryString}`, get());

    if (!response.ok) {
        await throwHttpError(
            'An error occurred while getting the point views for topic.',
            response
        );
    }

    logWithAmplitude(amplitude_callsite, 'Get Point Views For Topic', {
        topic_id,
    });
    return await (response.json() as Promise<{ [key: string]: number }>);
}

export async function getOpenTableClicksForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/opentable_clicks${queryString}`, get());

    if (!response.ok) {
        await throwHttpError(
            'An error occurred while getting the OpenTable clicks for topic.',
            response
        );
    }

    logWithAmplitude(amplitude_callsite, 'Get OpenTable Clicks For Topic', {
        topic_id,
    });
    return await (response.json() as Promise<{ [key: string]: number }>);
}

export async function getBookmarkCountForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/bookmark_count${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occured while getting the bookmark count.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Bookmark Count', {
        topic_id,
        topic_key,
    });
    return await (response.json() as Promise<number>);
}

export async function getAllRendersForTopic(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/renders${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the renders for topic.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get All Renders For Topic', {
        topic_id,
    });
    return await (response.json() as Promise<TelemetryEntry[]>);
}

export async function sendInviteEmails(
    amplitude_callsite: string,
    topic_id: string,
    access_level: TopicAccessLevel,
    emails: string[],
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
        const response = await fetch(
        `/api/topics/${topic_id}/send_invite_email/${access_level}${queryString}`,
        post(emails)
    );
    if (!response.ok) {
        await throwHttpError('An error occurred while sending the invite emails.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Send Invite Email', {
        topic_id,
        access_level,
        emails_input: emails.length,
    });
    return await Promise.resolve();
}

export function getEmbedCode(
    amplitude_callsite: string,
    link: string,
    removeProxiBranding: boolean
) {
    let embed_code = IFRAME_TEMPLATE.replace('@@@@@', link);
    if (!removeProxiBranding) {
        embed_code = embed_code + POWERED_BY_EMBED;
    }
    return embed_code;
}

export async function getRedirectKeyAvailable(amplitude_callsite: string, redirect_key: string): Promise<boolean> {
    const response = await fetch(`/r/${redirect_key}/available`, get());

    if (!response.ok) {
        await throwHttpError(
            'An error occurred while getting the redirect key availability.',
            response
        );
    }

    logWithAmplitude(amplitude_callsite, 'Redirect Key Available', {
        redirect_key,
    });
    return await (response.json() as Promise<boolean>);
}

export function createTopicRequestFromTopic(t?: GetTopicResponse): CreateTopicRequest {
    if (t === undefined) {
        throw new Error('Cannot create topic request from undefined topic.');
    }
    const tag_info: CreateTagRequest = {
        user_tags: t.tag_info?.user_tags ?? [],
    };

    return {
        name: t.name ?? '',
        email: t.email,
        organization_id: t.organization_id,
        use_organization_template: false, // Because we are creating a topic from another, do not use template
        creator: t.creator,
        categories: t.categories,
        has_dates: t.has_dates,
        has_date_only: t.has_date_only,
        has_time_only: t.has_time_only,
        show_easy_date_filters: t.show_easy_date_filters,
        has_end_date_time: t.has_end_date_time,
        should_filter_past_events: t.should_filter_past_events,
        has_likes: t.has_likes,
        discovery_level: t.discovery_level,
        disable_google_images: t.disable_google_images,
        hide_header: t.hide_header,
        show_advanced_filter_row: t.show_advanced_filter_row,
        custom_font: t.custom_font,
        show_point_list_on_open: t.show_point_list_on_open,
        point_sort_order: t.point_sort_order,
        disable_location_ui: t.disable_location_ui,
        disable_build_your_own: t.disable_build_your_own,
        disable_command_center_buttons: t.disable_command_center_buttons,
        requires_approval: t.requires_approval,
        image_display: t.image_display,
        default_list_view: t.default_list_view,
        default_shape_style: t.default_shape_style,
        search_pill_settings: t.search_pill_settings,
        share_settings: t.share_settings,
        send_crowdsource_email: t.send_crowdsource_email,
        enable_point_numbering: t.enable_point_numbering,
        replace_icons_with_numbers: t.replace_icons_with_numbers,
        point_number_circle_color: t.point_number_circle_color,
        marker_shape: t.marker_shape,
        default_map_center: t.default_map_center,
        default_map_zoom: t.default_map_zoom,
        default_map_rotation: t.default_map_rotation,
        default_map_tilt: t.default_map_tilt,
        marker_cluster_threshold: t.marker_cluster_threshold,
        marker_cluster_minimum: t.marker_cluster_minimum,
        icon_size: t.icon_size,
        hide_featured_on: t.hide_featured_on,
        embed_gesture_greedy: t.embed_gesture_greedy,
        embed_all_description_links: t.embed_all_description_links,
        filter_featured_on: t.filter_featured_on,
        featured_on_creator_profiles: t.featured_on_creator_profiles,
        enable_panning: t.enable_panning,
        header_logo: t.header_logo,
        header_url: t.header_url,
        header_color: t.header_color,
        header_text_color: t.header_text_color,
        cluster_accent_color: t.cluster_accent_color,
        translation_languages: t.translation_languages,
        default_language: t.default_language,
        featured_color: t.featured_color,
        featured_name: t.featured_name,
        custom_map_style: t.custom_map_style,
        custom_map_style_name: t.custom_map_style_name,
        custom_map_geojson: t.custom_map_geojson,
        custom_map_geojson_style: t.custom_map_geojson_style,
        custom_shapes: t.custom_shapes,
        custom_overlays: t.custom_overlays,
        custom_social_media_image: t.custom_social_media_image,
        description: t.description,
        tag_info,
        add_point_customization: t.add_point_customization,
        pop_ups: t.pop_ups,
        comment_settings: t.comment_settings,
        active_add_ons: t.active_add_ons,
        hidden_add_ons: t.hidden_add_ons,
        gates: t.gates,
        point_name: t.point_name,
        text_get_directions: t.text_get_directions,
        type_get_directions: t.type_get_directions,
        text_mark_as_visited: t.text_mark_as_visited,
        text_visited: t.text_visited,
        text_book_experiences: t.text_book_experiences,
        text_more_info: t.text_more_info,
        disable_get_directions: t.disable_get_directions,
        disable_add_to_map: t.disable_add_to_map,
        disable_more_info: t.disable_more_info,
        disable_mark_as_visited: t.disable_mark_as_visited,
        disable_book_experiences: t.disable_book_experiences,
        disable_share_button: t.disable_share_button,
        disable_list_button: t.disable_list_button,
        disable_search_button: t.disable_search_button,
        disable_powered_by_proxi: t.disable_powered_by_proxi,
        hide_bookmark_buttons: t.hide_bookmark_buttons,
        hide_print_button: t.hide_print_button,
        hide_get_route: t.hide_get_route,
        hide_multi_select: t.hide_multi_select,
        hide_map_info_drawer: t.hide_map_info_drawer,
        hide_send_map_to_phone: t.hide_send_map_to_phone,
        hide_map_stats_in_info_drawer: t.hide_map_stats_in_info_drawer,
        use_simple_filters_desktop: t.use_simple_filters_desktop,
        use_simple_filters_mobile: t.use_simple_filters_mobile,
        text_advanced_filters: t.text_advanced_filters,
        hide_category_filters: t.hide_category_filters,
        hide_tag_filters: t.hide_tag_filters,
        hide_keyword_filters: t.hide_keyword_filters,
        open_links_in_same_tab: t.open_links_in_same_tab,
        point_email_capture_customization: t.point_email_capture_customization,
        webhooks: t.webhooks,
    };
}

